import { Link } from "react-router-dom";
import { Mail, Instagram } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { CHECKUP_URL } from "../../config";

const COLS = [
  {
    title: "The Clinic",
    links: [
      { label: "About", to: "/about" },
      { label: "Insights", to: "/insights" },
      { label: "Get Your Free Check-Up", to: CHECKUP_URL, external: CHECKUP_URL.startsWith("http") },
    ],
  },
  {
    title: "The Visit",
    links: [
      { label: "The AI Business Check-Up", to: CHECKUP_URL, external: CHECKUP_URL.startsWith("http") },
      { label: "The Checkup Process", to: "/#process" },
      { label: "The Symptoms", to: "/#symptoms" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms of Use", to: "/terms" },
      { label: "Cookie Policy", to: "/cookies" },
      { label: "Cookie Settings", action: "open-cookie-settings" },
    ],
  },
];

export const Footer = () => {
  return (
    <footer className="relative z-10 border-t border-white/10 bg-[#0E1526]" data-testid="site-footer">
      <div className="shell py-20 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <div className="leading-[0.95] mb-6">
              <span className="block font-mono text-[16px] tracking-[0.3em] text-[#f5f3ee]">THE AI DOCTOR</span>
              <span className="block font-mono text-[10px] tracking-[0.24em] text-[#5CCBF5]">DIAGNOSIS FIRST.</span>
            </div>
            <p className="text-[#a7a7a2] text-lg max-w-xs leading-relaxed">
              A second opinion on where AI actually helps. No jargon. No 40-tab rabbit hole.
            </p>
            <div className="mt-10 flex flex-col gap-4" data-testid="footer-contact">
              <a href="mailto:info@theaidoctor.website" data-testid="footer-email" className="group inline-flex items-center gap-4">
                <span className="w-12 h-12 border border-[#5CCBF5]/40 bg-[#131C31] flex items-center justify-center text-[#5CCBF5] group-hover:bg-[#5CCBF5] group-hover:text-[#0E1526] transition-colors duration-300">
                  <Mail size={18} />
                </span>
                <span className="font-mono text-[13px] tracking-[0.06em] text-[#f5f3ee] group-hover:text-[#5CCBF5] transition-colors duration-300">info@theaidoctor.website</span>
              </a>
              <a href="https://instagram.com/theaidoctorofficial" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="group inline-flex items-center gap-4">
                <span className="w-12 h-12 border border-[#5CCBF5]/40 bg-[#131C31] flex items-center justify-center text-[#5CCBF5] group-hover:bg-[#5CCBF5] group-hover:text-[#0E1526] transition-colors duration-300">
                  <Instagram size={18} />
                </span>
                <span className="font-mono text-[13px] tracking-[0.06em] text-[#f5f3ee] group-hover:text-[#5CCBF5] transition-colors duration-300">@theaidoctorofficial</span>
              </a>
              <a href="https://wa.me/33770241597" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="group inline-flex items-center gap-4">
                <span className="w-12 h-12 border border-[#5CCBF5]/40 bg-[#131C31] flex items-center justify-center text-[#5CCBF5] group-hover:bg-[#5CCBF5] group-hover:text-[#0E1526] transition-colors duration-300">
                  <WhatsAppIcon size={18} />
                </span>
                <span className="font-mono text-[13px] tracking-[0.06em] text-[#f5f3ee] group-hover:text-[#5CCBF5] transition-colors duration-300">+33 7 70 24 15 97</span>
              </a>
            </div>
          </div>
          {COLS.map((c) => (
            <div key={c.title}>
              <p className="eyebrow-muted mb-5">{c.title}</p>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l.label}>
                    {l.action ? (
                      <button
                        type="button"
                        onClick={() => window.dispatchEvent(new Event(l.action))}
                        data-testid="footer-cookie-settings"
                        className="link-underline text-[#f5f3ee] text-[15px] hover:text-[#5CCBF5] transition-colors duration-300 text-left"
                      >
                        {l.label}
                      </button>
                    ) : l.external ? (
                      <a
                        href={l.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-underline text-[#f5f3ee] text-[15px] hover:text-[#5CCBF5] transition-colors duration-300"
                      >
                        {l.label}
                      </a>
                    ) : (
                      <Link
                        to={l.to}
                        className="link-underline text-[#f5f3ee] text-[15px] hover:text-[#5CCBF5] transition-colors duration-300"
                      >
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-white/10">
          <p className="display text-3xl sm:text-4xl lg:text-5xl text-[#f5f3ee] max-w-4xl">
            You didn't start a business to answer the same DM at 11pm.
            <span className="text-[#a7a7a2]"> Get the diagnosis. Skip the 40 tabs. Keep your evenings.</span>
          </p>
          <p className="mt-10 font-mono text-[11px] tracking-[0.2em] uppercase text-[#5c5c58]">
            © 2026 The AI Doctor. All rights reserved. · Calls scheduled in US business hours.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
